import React, { useState, useEffect, useRef } from 'react';
import Slider from 'react-slick';
import "slick-carousel/slick/slick.css"; 
import "slick-carousel/slick/slick-theme.css";
import { Search, CalendarDays, Video, Brain, Star, ChevronRight, ChevronLeft } from 'lucide-react';
import './HowItWorksSteps.css';

const steps = [
  {
    id: 1,
    icon: <Search strokeWidth={2.5} size={32} />,
    title: 'Find & Apply',
    desc: 'Browse verified roles\nthat match your profile\nand apply in one click.',
    color: '#3b82f6', // Blue
    glowColor: 'rgba(59, 130, 246, 0.6)',
    borderColor: 'rgba(59, 130, 246, 0.4)'
  },
  {
    id: 2,
    icon: <CalendarDays strokeWidth={2.5} size={32} />,
    title: 'Book Your Slot',
    desc: 'Choose a convenient\ntime slot and confirm\nyour audition.',
    color: '#ef4444', // Red
    glowColor: 'rgba(239, 68, 68, 0.6)',
    borderColor: 'rgba(239, 68, 68, 0.4)'
  },
  {
    id: 3,
    icon: <Video strokeWidth={2.5} size={32} />,
    title: 'Prepare & Audition',
    desc: 'Prepare well, record or\nattend your audition and\nshow your best.',
    color: '#10b981', // Green
    glowColor: 'rgba(16, 185, 129, 0.6)',
    borderColor: 'rgba(16, 185, 129, 0.4)'
  },
  {
    id: 4,
    icon: <Brain strokeWidth={2.5} size={32} />,
    title: 'Get AI Feedback',
    desc: 'Receive AI performance\nanalysis and expert\nimprovement tips.',
    color: '#f97316', // Orange
    glowColor: 'rgba(249, 115, 22, 0.6)',
    borderColor: 'rgba(249, 115, 22, 0.4)'
  },
  {
    id: 5,
    icon: <Star strokeWidth={2.5} size={32} />,
    title: 'Get Discovered',
    desc: 'Get shortlisted by\ndirectors and move\ncloser to success.',
    color: '#a855f7', // Purple
    glowColor: 'rgba(168, 85, 247, 0.6)',
    borderColor: 'rgba(168, 85, 247, 0.4)'
  }
];

const HowItWorksSteps = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 991);
  const sliderRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 991);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const renderCardContent = (step) => (
    <div 
      className="hiw-step-card glossy-card mx-auto"
      style={{ 
        '--step-color': step.color,
        '--step-glow': step.glowColor,
        '--step-border': step.borderColor
      }}
    >
      {/* Number Badge overlapping top border */}
      <div className="hiw-step-number" style={{ 
        backgroundColor: step.color, 
        boxShadow: `0 0 15px 2px ${step.glowColor}, inset 0 0 10px rgba(255,255,255,0.5)`
      }}>
        {step.id}
      </div>

      <div className="hiw-step-content">
        <div className="hiw-step-icon-wrapper">
          <div className="hiw-step-icon-bg" style={{ boxShadow: `inset 0 0 25px ${step.glowColor}` }}></div>
          <div className="hiw-step-icon" style={{ color: step.color }}>
            {step.icon}
          </div>
        </div>
        
        <h4 className="hiw-step-title">{step.title}</h4>
        
        <div className="hiw-step-divider" style={{ backgroundColor: step.color, boxShadow: `0 0 8px ${step.glowColor}` }}></div>
        
        <p className="hiw-step-text">
          {step.desc.split('\n').map((line, i) => (
            <React.Fragment key={i}>
              {line}<br/>
            </React.Fragment>
          ))}
        </p>
        
        {/* Dots indicator at bottom */}
        <div className="hiw-step-dots">
          {[1, 2, 3, 4, 5].map((num) => (
            <div 
              key={num} 
              className={`hiw-dot ${num === step.id ? 'active' : ''}`}
              style={{ 
                backgroundColor: num === step.id ? step.color : '#334155',
                boxShadow: num === step.id ? `0 0 10px ${step.glowColor}` : 'none'
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <section className="hiw-section">
      <div className="container">
        
        {/* Header */}
        <div className="hiw-header text-center">
          <div className="hiw-subtitle-wrapper">
            <span className="hiw-line"></span>
            <span className="hiw-subtitle">HOW IT WORKS</span>
            <span className="hiw-line"></span>
          </div>
          <h2 className="hiw-title">
            Your Journey. <span className="hiw-title-highlight">Step by Step.</span>
          </h2>
          <p className="hiw-desc">A simple 5-step process to help you go from aspiring to selected.</p>
        </div>

        {isMobile ? (
          <div style={{ position: 'relative' }}>
            <div className="d-flex justify-content-end mb-3 gap-2" style={{ paddingRight: '15px' }}>
              <button onClick={() => sliderRef.current?.slickPrev()} className="btn-nav-arrow" style={{ background: 'transparent', border: 'none', color: '#fff' }}><ChevronLeft size={24}/></button>
              <button onClick={() => sliderRef.current?.slickNext()} className="btn-nav-arrow" style={{ background: 'transparent', border: 'none', color: '#fff' }}><ChevronRight size={24}/></button>
            </div>
            <Slider ref={sliderRef} arrows={false} infinite={false} slidesToShow={1.4} speed={500} dots={false} swipeToSlide={true}>
              {steps.map((step) => (
                <div key={step.id} style={{ padding: '30px 10px 40px 10px' }}>
                  {renderCardContent(step)}
                </div>
              ))}
            </Slider>
          </div>
        ) : (
          <div className="hiw-steps-container">
            {steps.map((step, index) => (
              <div className="hiw-step-card-wrapper" key={step.id}>
                
                {renderCardContent(step)}

                {/* Connecting Arrow between cards */}
                {index < steps.length - 1 && (
                  <div className="hiw-step-connector">
                     <div className="connector-line" style={{ 
                       background: `linear-gradient(90deg, ${step.color}, ${steps[index+1].color})`,
                       boxShadow: `0 0 8px ${step.color}`
                     }}>
                        <div className="connector-glow-dot" style={{ backgroundColor: step.color, boxShadow: `0 0 15px 4px ${step.glowColor}` }}></div>
                        <div className="connector-glow-dot right" style={{ backgroundColor: steps[index+1].color, boxShadow: `0 0 15px 4px ${steps[index+1].glowColor}` }}></div>
                        <div className="connector-arrow-circle">
                          <ChevronRight size={14} color="#fff" />
                        </div>
                     </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

      </div>
    </section>
  );
};

export default HowItWorksSteps;
